import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  NumberInput,
  NumberInputField,
  Textarea,
  useToast,
  Heading,
  Divider,
  Grid,
  GridItem,
  Text,
  InputGroup,
  InputLeftElement,
  Icon,
  List,
  ListItem,
  Badge,
  Flex,
  FormHelperText,
} from '@chakra-ui/react';
import { MdSearch, MdPerson } from 'react-icons/md';
import {
  createEmployeeConfig,
  getEmployeeConfig,
  updateEmployeeConfig,
} from '../../services/payroll-extended.service';
import { getAllEmployees } from '../../services/api';

function currentUserId() {
  try {
    const raw = localStorage.getItem('userProfile');
    const user = raw ? JSON.parse(raw) : null;
    const id = Number(user?.userId || user?.id);
    return Number.isFinite(id) && id > 0 ? id : undefined;
  } catch {
    return undefined;
  }
}

const EmployeePayrollConfig = ({ 
  employeeId: propEmployeeId, 
  employeeName: propEmployeeName, 
  preselectedEmployeeId,
  preselectedEmployeeName,
  onSuccess 
}) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [configExists, setConfigExists] = useState(false);
  const [configId, setConfigId] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(
    preselectedEmployeeId || propEmployeeId || ''
  );
  const [selectedEmployeeName, setSelectedEmployeeName] = useState(
    preselectedEmployeeName || propEmployeeName || ''
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [showEmployeeList, setShowEmployeeList] = useState(false);

  const [formData, setFormData] = useState({
    baseSalary: '',
    salaryType: 'MONTHLY',
    hourlyRate: '',
    dailyRate: '',
    standardHoursPerMonth: 200,
    overtimeRate50: 1.50,
    overtimeRate100: 2.00,
    fonasaType: 'base',
    bankAccount: '',
    paymentMethod: 'TRANSFER',
    notes: '',
    effectiveFrom: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    loadEmployees();
  }, []);

  useEffect(() => {
    if (selectedEmployeeId) {
      console.log('🎯 useEffect disparado, cargando config para:', selectedEmployeeId);
      loadEmployeeConfig();
    }
  }, [selectedEmployeeId]);

  useEffect(() => {
    if (preselectedEmployeeId) {
      console.log('🎯 Empleado preseleccionado recibido:', preselectedEmployeeId);
      setSelectedEmployeeId(preselectedEmployeeId);
      setSelectedEmployeeName(preselectedEmployeeName || '');
    }
  }, [preselectedEmployeeId, preselectedEmployeeName]);

  const loadEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const response = await getAllEmployees();
      console.log('Response getAllEmployees:', response);
      
      let employeeList = [];
      
      if (response?.payload?.data) {
        employeeList = response.payload.data;
      } else if (Array.isArray(response?.data)) {
        employeeList = response.data;
      } else if (Array.isArray(response)) {
        employeeList = response;
      }
      
      console.log('Employees loaded:', employeeList);
      setEmployees(employeeList);
      
      if (employeeList.length === 0) {
        toast({
          title: 'Aviso',
          description: 'No se encontraron empleados',
          status: 'warning',
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Error cargando empleados:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los empleados',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoadingEmployees(false);
    }
  };

  const loadEmployeeConfig = async () => {
    if (!selectedEmployeeId) return;
    
    console.log('🔍 Cargando configuración para empleado:', selectedEmployeeId);
    
    try {
      const response = await getEmployeeConfig(selectedEmployeeId);
      console.log('📦 Respuesta completa:', response);
      
      // httpClient retorna response.data directamente
      // La estructura es: { status, message, payload: { data: {...} } }
      if (response?.status === 200 && response?.payload?.data) {
        const config = response.payload.data;
        console.log('✅ Configuración encontrada:', config);
        setConfigExists(true);
        setConfigId(config.id);
        
        // Determinar tipo de FONASA según configuración existente
        let fonasaType = 'base';
        if (config.has_spouse && config.children_count >= 1) {
          fonasaType = 'with_spouse_and_children';
        } else if (config.has_spouse) {
          fonasaType = 'with_spouse';
        } else if (config.children_count >= 3) {
          fonasaType = 'three_or_more_children';
        } else if (config.children_count === 2) {
          fonasaType = 'two_children';
        } else if (config.children_count === 1) {
          fonasaType = 'one_child';
        }
        
        setFormData({
          baseSalary: config.base_salary || '',
          salaryType: config.salary_type || 'MONTHLY',
          hourlyRate: config.hourly_rate || '',
          dailyRate: config.daily_rate || '',
          standardHoursPerMonth: config.standard_hours_per_month || 200,
          overtimeRate50: config.overtime_rate_50 || 1.50,
          overtimeRate100: config.overtime_rate_100 || 2.00,
          fonasaType: fonasaType,
          bankAccount: config.bank_account || '',
          paymentMethod: config.payment_method || 'TRANSFER',
          notes: config.notes || '',
          effectiveFrom: config.effective_from?.split('T')[0] || new Date().toISOString().split('T')[0],
        });
        console.log('✅ FormData actualizado correctamente');
      } else {
        console.log('⚠️ No se encontró configuración en la respuesta');
        // No hay configuración previa, mantener valores por defecto
        setConfigExists(false);
        setConfigId(null);
      }
    } catch (error) {
      console.log('❌ Error al cargar configuración:', error);
      // No hay configuración o error, mantener valores por defecto
      setConfigExists(false);
      setConfigId(null);
      setFormData({
        baseSalary: '',
        salaryType: 'MONTHLY',
        hourlyRate: '',
        dailyRate: '',
        standardHoursPerMonth: 200,
        overtimeRate50: 1.50,
        overtimeRate100: 2.00,
        fonasaType: 'base',
        bankAccount: '',
        paymentMethod: 'TRANSFER',
        notes: '',
        effectiveFrom: new Date().toISOString().split('T')[0],
      });
    }
  };

  const handleEmployeeChange = (empId) => {
    setSelectedEmployeeId(empId);
    
    if (empId) {
      const employee = employees.find(emp => emp.id === parseInt(empId));
      if (employee) {
        const fullName = `${employee.person.name} ${employee.person.surname}`;
        setSelectedEmployeeName(fullName);
        setSearchTerm(fullName);
      }
    } else {
      setSelectedEmployeeName('');
      setSearchTerm('');
    }
    setShowEmployeeList(false);
  };

  const filteredEmployees = useMemo(() => {
    if (!searchTerm) return employees;
    
    const term = searchTerm.toLowerCase();
    return employees.filter(emp => {
      const fullName = `${emp.person.name} ${emp.person.surname}`.toLowerCase();
      const document = emp.person.documentNumber?.toLowerCase() || '';
      const type = emp.employeeType?.name?.toLowerCase() || '';
      
      return fullName.includes(term) || document.includes(term) || type.includes(term);
    });
  }, [employees, searchTerm]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!selectedEmployeeId) {
        toast({
          title: 'Error',
          description: 'Debe seleccionar un empleado',
          status: 'error',
          duration: 3000,
        });
        return;
      }

      const data = {
        employeeId: parseInt(selectedEmployeeId),
        baseSalary: parseFloat(formData.baseSalary),
        salaryType: formData.salaryType,
        standardHoursPerMonth: parseInt(formData.standardHoursPerMonth),
        overtimeRate50: parseFloat(formData.overtimeRate50),
        overtimeRate100: parseFloat(formData.overtimeRate100),
        fonasaType: formData.fonasaType,
        bankAccount: formData.bankAccount || null,
        paymentMethod: formData.paymentMethod,
        notes: formData.notes || null,
        effectiveFrom: formData.effectiveFrom,
        createdBy: currentUserId(),
      };

      if (formData.hourlyRate) data.hourlyRate = parseFloat(formData.hourlyRate);
      if (formData.dailyRate) data.dailyRate = parseFloat(formData.dailyRate);

      let response;
      if (configExists && configId) {
        data.updatedBy = 1;
        response = await updateEmployeeConfig(configId, data);
      } else {
        response = await createEmployeeConfig(data);
      }

      // httpClient retorna response.data directamente
      if (response?.status === 200 || response?.status === 201) {
        toast({
          title: 'Éxito',
          description: response.message || 'Configuración guardada correctamente',
          status: 'success',
          duration: 3000,
        });
        if (onSuccess) onSuccess();
        await loadEmployeeConfig();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Error al guardar configuración',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const getFonasaRateInfo = () => {
    const rates = {
      'base': { label: 'FONASA Base', rate: '4.5%', description: 'Sin cargas familiares' },
      'with_spouse': { label: 'FONASA con Cónyuge', rate: '6.0%', description: 'Con cónyuge a cargo, sin hijos' },
      'one_child': { label: 'FONASA con 1 Hijo', rate: '5.0%', description: 'Con 1 hijo a cargo' },
      'two_children': { label: 'FONASA con 2 Hijos', rate: '6.0%', description: 'Con 2 hijos a cargo' },
      'three_or_more_children': { label: 'FONASA con 3+ Hijos', rate: '8.0%', description: 'Con 3 o más hijos a cargo' },
      'with_spouse_and_children': { label: 'FONASA con Cónyuge e Hijos', rate: '8.0%', description: 'Con cónyuge e hijos a cargo' },
    };
    return rates[formData.fonasaType] || rates['base'];
  };

  const rateInfo = getFonasaRateInfo();

  return (
    <Box>
      <Heading size="md" mb={4}>
        Configuración de Liquidación{selectedEmployeeName && ` - ${selectedEmployeeName}`}
      </Heading>

      <form onSubmit={handleSubmit}>
        <VStack spacing={6} align="stretch">
          {/* Selector de Empleado con Búsqueda - Solo mostrar si NO viene preseleccionado */}
          {!preselectedEmployeeId && (
            <Box bg="blue.50" p={4} borderRadius="md">
              <FormControl isRequired>
                <FormLabel fontWeight="bold" fontSize="lg">
                  <HStack>
                    <Icon as={MdPerson} />
                    <Text>Empleado</Text>
                  </HStack>
                </FormLabel>
                
                {/* Input de búsqueda */}
                <InputGroup size="lg" mb={2}>
                  <InputLeftElement>
                    <Icon as={MdSearch} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Buscar por nombre, apellido o documento..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowEmployeeList(true);
                    }}
                    onFocus={() => setShowEmployeeList(true)}
                    bg="white"
                    isDisabled={loadingEmployees}
                  />
                </InputGroup>

                {/* Lista de empleados filtrados */}
                {showEmployeeList && filteredEmployees.length > 0 && (
                  <Box
                    maxH="300px"
                    overflowY="auto"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    bg="white"
                    position="relative"
                    zIndex={10}
                  >
                    <List spacing={0}>
                      {filteredEmployees.map((emp) => (
                        <ListItem
                          key={emp.id}
                          p={3}
                          cursor="pointer"
                          _hover={{ bg: 'blue.50' }}
                          borderBottom="1px solid"
                          borderColor="gray.100"
                          onClick={() => handleEmployeeChange(emp.id)}
                        >
                          <HStack justify="space-between">
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="bold">
                                {emp.person.name} {emp.person.surname}
                              </Text>
                              <Text fontSize="sm" color="gray.600">
                                {emp.person.documentType?.name}: {emp.person.documentNumber}
                              </Text>
                            </VStack>
                            <Badge colorScheme="blue">
                              {emp.employeeType?.name}
                            </Badge>
                          </HStack>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Empleado seleccionado */}
                {selectedEmployeeId && (
                  <Box mt={3} p={3} bg="white" borderRadius="md" borderWidth="1px">
                    <HStack justify="space-between">
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="bold" fontSize="sm" color="gray.600">
                          SELECCIONADO:
                        </Text>
                        <Text fontWeight="bold">{selectedEmployeeName}</Text>
                      </VStack>
                      <Button
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => {
                          setSelectedEmployeeId('');
                          setSelectedEmployeeName('');
                          setSearchTerm('');
                        }}
                      >
                        Cambiar
                      </Button>
                    </HStack>
                    
                    {configExists && (
                      <Text mt={2} fontSize="sm" color="green.600">
                        ✅ Este empleado ya tiene una configuración (se actualizará)
                      </Text>
                    )}
                    {!configExists && (
                      <Text mt={2} fontSize="sm" color="orange.600">
                        ⚠️ Este empleado no tiene configuración (se creará nueva)
                      </Text>
                    )}
                  </Box>
                )}

                {filteredEmployees.length === 0 && searchTerm && (
                  <Text mt={2} color="gray.500" fontSize="sm">
                    No se encontraron empleados que coincidan con "{searchTerm}"
                  </Text>
                )}
              </FormControl>
            </Box>
          )}

          {/* Mostrar info del empleado preseleccionado */}
          {preselectedEmployeeId && (
            <Box bg="blue.50" p={4} borderRadius="md">
              <HStack spacing={3}>
                <Icon as={MdPerson} boxSize={6} color="blue.500" />
                <VStack align="start" spacing={0} flex={1}>
                  <Text fontWeight="bold" fontSize="sm" color="gray.600">
                    CONFIGURACIÓN PARA:
                  </Text>
                  <Text fontWeight="bold" fontSize="lg">{selectedEmployeeName}</Text>
                  {configExists && (
                    <Text mt={1} fontSize="sm" color="green.600">
                      ✅ Ya tiene configuración (se actualizará al guardar)
                    </Text>
                  )}
                  {!configExists && (
                    <Text mt={1} fontSize="sm" color="orange.600">
                      ⚠️ Sin configuración previa (se creará nueva)
                    </Text>
                  )}
                </VStack>
              </HStack>
            </Box>
          )}

          <Divider />

          {/* Salario Base */}
          <Box>
            <Heading size="sm" mb={3}>Salario Base</Heading>
            <Grid templateColumns="repeat(3, 1fr)" gap={4}>
              <GridItem>
                <FormControl isRequired>
                  <FormLabel>Tipo de Salario</FormLabel>
                  <Select
                    value={formData.salaryType}
                    onChange={(e) => handleInputChange('salaryType', e.target.value)}
                  >
                    <option value="MONTHLY">Mensual</option>
                    <option value="HOURLY">Por Hora</option>
                    <option value="DAILY">Por Día</option>
                  </Select>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl isRequired>
                  <FormLabel>Salario Base</FormLabel>
                  <NumberInput
                    value={formData.baseSalary}
                    onChange={(value) => handleInputChange('baseSalary', value)}
                    min={0}
                  >
                    <NumberInputField placeholder="50000" />
                  </NumberInput>
                </FormControl>
              </GridItem>

              {formData.salaryType === 'HOURLY' && (
                <GridItem>
                  <FormControl>
                    <FormLabel>Tarifa por Hora</FormLabel>
                    <NumberInput
                      value={formData.hourlyRate}
                      onChange={(value) => handleInputChange('hourlyRate', value)}
                      min={0}
                    >
                      <NumberInputField placeholder="250" />
                    </NumberInput>
                  </FormControl>
                </GridItem>
              )}

              {formData.salaryType === 'DAILY' && (
                <GridItem>
                  <FormControl>
                    <FormLabel>Tarifa por Día</FormLabel>
                    <NumberInput
                      value={formData.dailyRate}
                      onChange={(value) => handleInputChange('dailyRate', value)}
                      min={0}
                    >
                      <NumberInputField placeholder="1666" />
                    </NumberInput>
                  </FormControl>
                </GridItem>
              )}
            </Grid>
          </Box>

          <Divider />

          {/* Horas Extras */}
          <Box>
            <Heading size="sm" mb={3}>Configuración de Horas</Heading>
            <Grid templateColumns="repeat(3, 1fr)" gap={4}>
              <GridItem>
                <FormControl>
                  <FormLabel>Horas Estándar/Mes</FormLabel>
                  <NumberInput
                    value={formData.standardHoursPerMonth}
                    onChange={(value) => handleInputChange('standardHoursPerMonth', value)}
                    min={1}
                  >
                    <NumberInputField />
                  </NumberInput>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel>Recargo Horas Extra 50%</FormLabel>
                  <NumberInput
                    value={formData.overtimeRate50}
                    onChange={(value) => handleInputChange('overtimeRate50', value)}
                    min={1}
                    step={0.1}
                    precision={2}
                  >
                    <NumberInputField />
                  </NumberInput>
                  <Text fontSize="xs" color="gray.500">Multiplicador (ej: 1.5 = 150%)</Text>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel>Recargo Horas Extra 100%</FormLabel>
                  <NumberInput
                    value={formData.overtimeRate100}
                    onChange={(value) => handleInputChange('overtimeRate100', value)}
                    min={1}
                    step={0.1}
                    precision={2}
                  >
                    <NumberInputField />
                  </NumberInput>
                  <Text fontSize="xs" color="gray.500">Para nocturnos, domingos</Text>
                </FormControl>
              </GridItem>
            </Grid>
          </Box>

          <Divider />

          {/* FONASA Type - NUEVA SECCIÓN REEMPLAZANDO DEDUCCIONES PERSONALIZADAS */}
          <Box>
            <Heading size="sm" mb={3}>Tipo de FONASA</Heading>
            <Text fontSize="sm" color="gray.600" mb={3}>
              Las tasas de BPS (15%) y FRL (0.10%) son globales. Solo debe configurar el tipo de FONASA del empleado.
            </Text>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <GridItem colSpan={2}>
                <FormControl isRequired>
                  <FormLabel fontWeight="bold">Tipo de FONASA</FormLabel>
                  <Select
                    value={formData.fonasaType}
                    onChange={(e) => handleInputChange('fonasaType', e.target.value)}
                    size="lg"
                  >
                    <option value="base">FONASA Base (4.5%) - Sin cargas</option>
                    <option value="with_spouse">FONASA con Cónyuge (6.0%) - Solo cónyuge</option>
                    <option value="one_child">FONASA con 1 Hijo (5.0%) - 1 hijo a cargo</option>
                    <option value="two_children">FONASA con 2 Hijos (6.0%) - 2 hijos a cargo</option>
                    <option value="three_or_more_children">FONASA con 3+ Hijos (8.0%) - 3 o más hijos</option>
                    <option value="with_spouse_and_children">FONASA con Cónyuge e Hijos (8.0%) - Cónyuge e hijos</option>
                  </Select>
                  <FormHelperText>
                    Seleccione el tipo según la situación familiar del empleado
                  </FormHelperText>
                </FormControl>
              </GridItem>
              
              <GridItem colSpan={2}>
                <Box
                  p={4}
                  bg="blue.50"
                  borderRadius="md"
                  borderLeft="4px solid"
                  borderColor="brand.500"
                >
                  <Flex align="center" justify="space-between">
                    <Box>
                      <Text fontWeight="bold" fontSize="md">
                        {rateInfo.label}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {rateInfo.description}
                      </Text>
                    </Box>
                    <Badge colorScheme="brand" fontSize="md" p={2}>
                      {rateInfo.rate}
                    </Badge>
                  </Flex>
                </Box>
              </GridItem>
            </Grid>
          </Box>

          <Divider />

          {/* Información Bancaria y Pago */}
          <Box>
            <Heading size="sm" mb={3}>Información de Pago</Heading>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <GridItem>
                <FormControl>
                  <FormLabel>Cuenta Bancaria</FormLabel>
                  <Input
                    value={formData.bankAccount}
                    onChange={(e) => handleInputChange('bankAccount', e.target.value)}
                    placeholder="001234567890"
                  />
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel>Método de Pago</FormLabel>
                  <Select
                    value={formData.paymentMethod}
                    onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                  >
                    <option value="TRANSFER">Transferencia</option>
                    <option value="CASH">Efectivo</option>
                    <option value="CHECK">Cheque</option>
                  </Select>
                </FormControl>
              </GridItem>
            </Grid>
          </Box>

          <Divider />

          {/* Vigencia y Notas */}
          <Box>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <GridItem>
                <FormControl isRequired>
                  <FormLabel>Fecha de Vigencia Desde</FormLabel>
                  <Input
                    type="date"
                    value={formData.effectiveFrom}
                    onChange={(e) => handleInputChange('effectiveFrom', e.target.value)}
                  />
                </FormControl>
              </GridItem>

              <GridItem colSpan={2}>
                <FormControl>
                  <FormLabel>Notas Adicionales</FormLabel>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    placeholder="Información adicional sobre la configuración..."
                    rows={3}
                  />
                </FormControl>
              </GridItem>
            </Grid>
          </Box>

          {/* Botones */}
          <HStack spacing={4} justify="flex-end">
            <Button
              colorScheme="blue"
              type="submit"
            >
              {configExists ? 'Actualizar Configuración' : 'Crear Configuración'}
            </Button>
          </HStack>
        </VStack>
      </form>
    </Box>
  );
};

export default EmployeePayrollConfig;
